import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

import type { ViewContextSlice, ViewContextSnapshotFn } from "~/ai-chat/model/view-context";

interface ChatViewContextValue {
  register: (key: string, snapshot: ViewContextSnapshotFn) => void;
  unregister: (key: string) => void;
  // Pulls every registered slice at the moment of send (see view-context.ts for
  // why pull-at-send rather than a captured render value).
  snapshot: () => ViewContextSlice[];
}

const ChatViewContext = createContext<ChatViewContextValue | null>(null);

export function ChatViewContextProvider({ children }: { children: React.ReactNode }) {
  const registryRef = useRef(new Map<string, ViewContextSnapshotFn>());

  const register = useCallback((key: string, snapshot: ViewContextSnapshotFn) => {
    registryRef.current.set(key, snapshot);
  }, []);

  const unregister = useCallback((key: string) => {
    registryRef.current.delete(key);
  }, []);

  const snapshot = useCallback(() => {
    const slices: ViewContextSlice[] = [];
    for (const snapshotFn of registryRef.current.values()) {
      const slice = snapshotFn();
      if (slice !== null) slices.push(slice);
    }
    return slices;
  }, []);

  const value = useMemo<ChatViewContextValue>(
    () => ({ register, unregister, snapshot }),
    [register, unregister, snapshot],
  );

  return <ChatViewContext.Provider value={value}>{children}</ChatViewContext.Provider>;
}

// Publishes a view's current state to the chat context. The snapshot fn is
// pulled at send time, so it should read live state/refs when called — capturing
// values in the closure would defeat the freshness the pull is for. Registration
// is stable across renders: the latest fn is held in a ref, so an inline closure
// doesn't thrash the registry.
export function useRegisterViewContext(key: string, snapshot: ViewContextSnapshotFn): void {
  const context = useContext(ChatViewContext);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  useEffect(() => {
    if (!context) return;
    const wrapper: ViewContextSnapshotFn = () => snapshotRef.current();
    context.register(key, wrapper);
    return () => context.unregister(key);
  }, [context, key]);
}

// Returns a stable function that snapshots all registered view slices. Tolerates
// an absent provider (returns an empty snapshot) so consumers and their tests
// work without wrapping in ChatViewContextProvider.
export function useChatViewSnapshot(): () => ViewContextSlice[] {
  const context = useContext(ChatViewContext);
  return useCallback(() => context?.snapshot() ?? [], [context]);
}

// True only when a ChatViewContextProvider is mounted — i.e. AI chat is enabled.
// Lets consumers skip work (e.g. the dev coverage warning) when there is no chat.
export function useHasChatViewContext(): boolean {
  return useContext(ChatViewContext) !== null;
}
